import Constants from "src/utils/constants";
import Exception from "src/utils/exception";
import Helper from "src/utils/helper";
import DBOperationsComb from "src/utils/dbOperationsComb";

const tableName =
  Constants.enums.TABLES_NAME_ENUM.MAIL_DISTRIBUTION_GROUP_TABLE;

/**
 * Lists the mail distribution groups.
 *
 * These ids are consumed all over the send path — `default_sending_data.to`
 * carries them as bare numbers and `Helper.convertGroupIdsToEmails` expands
 * them into member addresses — but nothing in the app has ever *listed* the
 * groups, so no UI could show a name for group `10`. This is that read path.
 *
 * Online-only: the table has no `remote_updated_at` / `local_updated_at` and is
 * absent from the sync index list, so it never reaches RxDB.
 *
 * @returns {Promise<{data: Array|null, totalItems: number, error: string|null}>}
 */
async function getMailDistributionGroups() {
  try {
    return await DBOperationsComb.fetchData(
      {
        tableName,
        fetchAll: true,
        // MUST pass a sortField. With none, fetchData falls back to
        // `.order("created_at")` (dbOperations/index.js:321) and this table has
        // no such column — it is only (id, is_deleted, name, description). The
        // query errors and the list comes back empty. Sorting by name is what a
        // picker wants anyway.
        sortField: "name",
        sortOrder: 1
        // fetchDeleted defaults to false, which applies is_deleted = false.
      },
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return { data: null, totalItems: 0, error };
  }
}

/**
 * Creates a distribution group.
 *
 * Groups are standalone, shared records — created from the email builder's
 * group picker and referenced by id from any number of templates.
 *
 * Online only, and without `created_by` / `updated_by` — this table has
 * neither column, and no local_updated_at (it is not in the sync layer).
 *
 * @param {Object} params
 * @param {string} params.name - Group name (required).
 * @param {string} [params.description]
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
async function createMailDistributionGroup({ name, description = null } = {}) {
  try {
    const trimmedName = Helper.blankToNull(name);
    if (!trimmedName) {
      return { error: "Distribution group name is required", data: null };
    }
    const res = await DBOperationsComb.insertData(
      tableName,
      {
        name: trimmedName,
        description: Helper.blankToNull(description),
        is_deleted: false
      },
      false,
      { attribution: false }
    );
    if (res?.error) return { error: res.error, data: null };
    return { error: null, data: res?.data ?? null };
  } catch (error) {
    Exception.catchException(error);
    return { error: error?.message || String(error), data: null };
  }
}

/**
 * Renames a distribution group / changes its description.
 *
 * A group is shared, so this changes it for every template that sends to it.
 * Same column options as create, plus no local_updated_at stamp.
 *
 * @param {number} id - The group's bigint identity id (0 is valid).
 * @param {Object} params
 * @param {string} params.name - Group name (required).
 * @param {string} [params.description]
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
async function updateMailDistributionGroup(
  id,
  { name, description = null } = {}
) {
  try {
    if (id === null || id === undefined || id === "") {
      return { error: "Distribution group ID is required", data: null };
    }
    const trimmedName = Helper.blankToNull(name);
    if (!trimmedName) {
      return { error: "Distribution group name is required", data: null };
    }
    const res = await DBOperationsComb.updateData(
      tableName,
      {
        id,
        name: trimmedName,
        description: Helper.blankToNull(description)
      },
      false,
      { attribution: false, syncStamp: false }
    );
    if (res?.error) return { error: res.error, data: null };
    return { error: null, data: res?.data ?? null };
  } catch (error) {
    Exception.catchException(error);
    return { error: error?.message || String(error), data: null };
  }
}

// Membership (profiles.mail_distributions_list) is written from the user
// profile drawer, not from this module.

export default {
  getMailDistributionGroups,
  createMailDistributionGroup,
  updateMailDistributionGroup
};
